import { observer } from "mobx-react-lite";
import { Button, Col, Input, InputNumber, Modal, Row, Select, Space, Upload } from "antd";
import React, { useEffect, useRef, useState } from "react";
import controller from "./controller";
import { InboxOutlined } from "@ant-design/icons";
import { SwitchBlock } from "../SwitchBlock";
import { SelectProduct } from "../../helpers/select_entity/SelectEntity";
import __select from "../../helpers/select_entity/__select.controller";
import i from "../../translations/i";

export const FunctionComponentTypeForm = observer(({ field, valueKey }: any) => {
    const keyForList = useRef(controller.randKey()).current
    const model = controller.models[valueKey] ?? {}

    if (typeof field.visible === 'function' && !field.visible(model)) {
        return <></>
    }

    if (typeof field.visible === 'string' && !model[field.visible]) {
        return <></>
    }

    if (field.type === 'input') return <FC_Input field={field} valueKey={valueKey} />
    else if (field.type === 'number') return <FC_Number field={field} valueKey={valueKey} />
    else if (field.type === 'select') return <FC_Select field={field} keyForList={keyForList} valueKey={valueKey} />
    else if (field.type === 'product_modal') return <FC_ProductModal field={field} valueKey={valueKey} />
    else if (field.type === 'file') return <FC_File field={field} valueKey={valueKey} />
    else if (field.type === 'switch') return <FC_Switch field={field} valueKey={valueKey} />

    return <></>
})

const FC_Input = observer(({ field, valueKey }: any) => {
    return (
        <Col span={field.span ?? 24}>
            <label>{field.label}</label>
            <Input
                value={controller.models[valueKey]?.[field.name] ?? ''}
                onChange={(e) => {
                    controller.models[valueKey][field.name] = e.target.value
                }}
            />
        </Col>
    )
})

const FC_Number = observer(({ field, valueKey }: any) => {
    return (
        <Col span={field.span ?? 24}>
            <label>{field.label}</label>
            <InputNumber
                value={controller.models[valueKey]?.[field.name] ?? null}
                onChange={(e) => {
                    controller.models[valueKey][field.name] = e
                }}
                style={{ width: '100%' }}
            />
        </Col>
    )
})

const FC_Select = observer(({ field, keyForList, valueKey }: any) => {

    useEffect(() => {
        if (field.requestData) {
            controller.request(field.requestData, keyForList)
        } else if (field.data) {
            controller.lists[keyForList] = field.data
        }
    }, [])

    const list = controller.lists[keyForList] ?? []
    const modelValue = controller.models[valueKey]?.[field.name]

    const value = field.multiple
        ? Array.isArray(modelValue)
            ? modelValue.map((el: any) => typeof el === 'object' ? el.id : el)
            : []
        : modelValue ?? undefined

    return (
        <Col span={field.span ?? 24}>
            <label>{field.label}</label>
            <Select
                mode={field.multiple ? 'multiple' : undefined}
                allowClear={field.allowClear ?? false}
                showSearch={field.showSearch ?? true}
                value={value}
                placeholder={field.placeholder ?? undefined}
                options={list.map((el: any) => ({
                    label: el[field.dataLabels.label],
                    value: el[field.dataLabels.id]
                }))}
                filterOption={(input, option: any) => {
                    return String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }}
                onChange={(e) => {
                    controller.models[valueKey][field.name] = e
                }}
                style={{ width: '100%' }}
            />
        </Col>
    )
})

const FC_ProductModal = observer(({ field, valueKey }: any) => {
    const [opened, setOpened] = useState(false)

    const selectedID = controller.models[valueKey]?.[field.name] ?? null
    const elementName = field.elementName ?? `_${field.name}_element`
    const selectedProduct = controller.models[valueKey]?.[elementName] ?? null

    const openModal = () => {
        __select.setType('product')
        __select.select.value = selectedID ?? ''
        __select.select.element = selectedProduct ?? null
        __select.element = selectedProduct ?? null
        setOpened(true)
    }

    const closeModal = () => {
        setOpened(false)
    }

    const saveProduct = () => {
        controller.models[valueKey][field.name] = __select.select.value ? Number(__select.select.value) : null
        controller.models[valueKey][elementName] = __select.select.element ?? __select.element ?? null
        setOpened(false)
    }

    const clearProduct = () => {
        controller.models[valueKey][field.name] = null
        controller.models[valueKey][elementName] = null
    }

    return (
        <Col span={field.span ?? 24}>
            <label>{field.label}</label>

            <Space>
                <Button type="dashed" onClick={openModal}>
                    {selectedID ? i.t('functionComponent:changeProduct') : i.t('functionComponent:selectProduct')}
                </Button>

                {selectedID && (
                    <Button danger type="dashed" onClick={clearProduct}>
                        {i.t('def:reset')}
                    </Button>
                )}
            </Space>

            {selectedID && (
                <div style={{ marginTop: 10 }}>
                    {i.t('functionComponent:selectedProduct', { id: selectedID })}
                    {selectedProduct?.name ? ` — ${selectedProduct.name}` : ''}
                </div>
            )}

            <Modal
                open={opened}
                footer={null}
                onCancel={closeModal}
                width={1000}
                centered={true}
                maskClosable={false}
            >
                <Button
                    type="primary"
                    onClick={saveProduct}
                    style={{ marginBottom: 20 }}
                >
                    {i.t('functionComponent:saveSelection')}
                </Button>

                <SelectProduct
                    elementID={__select.select.value}
                    element={__select.select.element}
                />
            </Modal>
        </Col>
    )
})

const FC_Switch = observer(({ field, valueKey }: any) => {
    return (
        <Col span={24}>
            <SwitchBlock
                array={field.items.map((item: any) => ({
                    label: item.label,
                    value: !!controller.models[valueKey]?.[item.name],
                    disabled: item.disabled ?? false,
                    click: (e: any) => {
                        controller.models[valueKey][item.name] = e

                        if (!e && item.clearFields && Array.isArray(item.clearFields)) {
                            item.clearFields.forEach((clearField: string) => {
                                controller.models[valueKey][clearField] = null
                            })
                        }
                    }
                }))}
            />
        </Col>
    )
})

const FC_File = observer(({ field, valueKey }: any) => {
    const value = controller.models[valueKey]?.[field.name]

    const getImageSrc = () => {
        if (!value || value instanceof File) return null

        if (value.path) return value.path

        if (value.base && value.uri) return value.base + value.uri

        if (value.base) return value.base

        if (typeof value === 'string') return value

        return null
    }

    const imageSrc = getImageSrc()

    return (
        <Col span={field.span ?? 24}>
            <label>{field.label}</label>

            <Row gutter={[20, 20]}>
                {imageSrc ? (
                    <Col span={24}>
                        <img
                            src={imageSrc}
                            alt=""
                            style={{ maxWidth: '250px', maxHeight: '250px' }}
                        />
                    </Col>
                ) : <></>}

                <Col span={24}>
                    <Upload
                        multiple={field.multiple ?? false}
                        fileList={value instanceof File ? [value as any] : []}
                        beforeUpload={(info: any) => {
                            controller.models[valueKey][field.name] = info
                            return false
                        }}
                    >
                        <Button icon={<InboxOutlined />}>{i.t('functionComponent:selectImage')}</Button>
                    </Upload>
                </Col>
            </Row>
        </Col>
    )
})